import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Image, Type, Upload as UploadIcon, X, AlertTriangle, ScanLine, Check, Edit3, Pause, Play, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { formatApiError } from "../utils/errorUtils";
import { trackContractUpload } from "../utils/analytics";

const Upload = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("file");
  const [contractName, setContractName] = useState("");
  const [file, setFile] = useState(null);
  const [pasteText, setPasteText] = useState("");
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [ocrWarning, setOcrWarning] = useState(null);
  
  // Camera/Photos state
  const [photos, setPhotos] = useState([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoProgress, setPhotoProgress] = useState({ current: 0, total: 0 });
  
  // OCR workflow state
  const [sessionKey, setSessionKey] = useState(null);
  const [ocrTotalPages, setOcrTotalPages] = useState(0);
  const [ocrStartPage, setOcrStartPage] = useState(1);
  const [ocrEndPage, setOcrEndPage] = useState(5);
  const [ocrJobId, setOcrJobId] = useState(null);
  const [ocrStatus, setOcrStatus] = useState(null);
  const [ocrEditedText, setOcrEditedText] = useState("");
  const [ocrStep, setOcrStep] = useState("upload"); // upload, range, processing, preview
  
  const pollingRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Poll for OCR status
  useEffect(() => {
    if (ocrJobId && ocrStep === "processing") {
      pollingRef.current = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/contracts/ocr/status/${ocrJobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setOcrStatus(response.data);
          
          if (response.data.status === "completed" || response.data.status === "failed") {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setOcrEditedText(response.data.text || "");
            setOcrStep("preview");
            
            if (response.data.status === "completed") {
              if (response.data.failed_pages?.length > 0) {
                toast.warning(`OCR completed with ${response.data.failed_pages.length} failed pages`);
              } else {
                toast.success("OCR completed! Review the extracted text.");
              }
            } else {
              toast.error("OCR failed. You can retry or try uploading as photos.");
            }
          } else if (response.data.status === "cancelled") {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setOcrStep("preview");
            setOcrEditedText(response.data.text || "");
            toast.info("OCR cancelled. You can resume or save partial results.");
          }
        } catch (error) {
          console.error("Polling error:", error);
        }
      }, 1500);
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [ocrJobId, ocrStep, token]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = (selectedFile) => {
    const isScannedTab = activeTab === "scanned";
    const validFileTypes = isScannedTab 
      ? ['application/pdf']
      : activeTab === "file" 
        ? ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain']
        : ['image/jpeg', 'image/png', 'image/webp'];
    
    // Also check file extension (some browsers report different MIME types for PDFs)
    const fileExtension = selectedFile.name.toLowerCase().split('.').pop();
    const isPdfByExtension = fileExtension === 'pdf';
    const isDocxByExtension = fileExtension === 'docx';
    const isTxtByExtension = fileExtension === 'txt';
    
    const isValidByExtension = (activeTab === "file" || isScannedTab) && 
      (isPdfByExtension || (activeTab === "file" && (isDocxByExtension || isTxtByExtension)));
    
    if (!validFileTypes.includes(selectedFile.type) && !isValidByExtension) {
      const expected = isScannedTab ? "PDF" : (activeTab === "file" ? "PDF, DOCX, or TXT" : "an image");
      toast.error(`Invalid file type. Please upload ${expected}`);
      return;
    }

    if (selectedFile.size > 20 * 1024 * 1024) {
      toast.error("File size must be less than 20MB");
      return;
    }

    setFile(selectedFile);
    if (!contractName) {
      setContractName(selectedFile.name.replace(/\.[^/.]+$/, ""));
    }
    
    resetOCRState();
  };

  const resetOCRState = () => {
    setSessionKey(null);
    setOcrJobId(null);
    setOcrStatus(null);
    setOcrEditedText("");
    setOcrStep("upload");
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleFileUpload = async () => {
    if (!file || !contractName) {
      toast.error("Please select a file and enter a name");
      return;
    }

    // File size check (50MB limit for mobile compatibility)
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 50MB. For larger files, try the Scanned PDF option with page ranges.`);
      return;
    }

    setLoading(true);
    setOcrWarning(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", contractName);

      // Determine correct endpoint based on file type
      let endpoint;
      const fileExtension = file.name.toLowerCase().split('.').pop();
      
      if (activeTab === "file") {
        if (fileExtension === 'pdf') {
          endpoint = "/contracts/upload-pdf";
        } else if (fileExtension === 'docx') {
          endpoint = "/contracts/upload-docx";
        } else if (fileExtension === 'txt') {
          endpoint = "/contracts/upload-text";
        } else {
          endpoint = "/contracts/upload-pdf"; // Default to PDF
        }
      } else {
        endpoint = "/contracts/upload-image";
      }
      
      // Increased timeout for mobile networks (5 minutes)
      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
        timeout: 300000, // 5 minutes timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      // Track contract upload (anonymous)
      trackContractUpload();
      
      toast.success("Contract uploaded successfully!");
      navigate(`/ask/${response.data.id}`);
    } catch (error) {
      console.error("Upload error:", error);
      
      // Better error handling for network issues
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error("Upload timed out. Please check your internet connection and try again. For large files, use the Scanned PDF option.");
      } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        toast.error("Network error. Please check your internet connection and try again.");
      } else {
        const errorMessage = formatApiError(error, "Upload failed. Please try again.");
        toast.error(errorMessage);
        
        if (file?.type === 'application/pdf' && errorMessage.toLowerCase().includes('scanned')) {
          setOcrWarning("This PDF appears to be scanned. Switch to the 'Scanned PDF' tab to use OCR.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzePDF = async () => {
    if (!file) {
      toast.error("Please select a PDF file");
      return;
    }

    setLoading(true);
    setOcrWarning(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/contracts/analyze-pdf`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      const { is_scanned, total_pages, session_key } = response.data;
      
      if (!is_scanned) {
        toast.info("This PDF has readable text. Use the 'File' tab for faster processing.");
        setOcrWarning("This PDF contains selectable text. Use the 'File' tab instead.");
      } else {
        setSessionKey(session_key);
        setOcrTotalPages(total_pages);
        setOcrStartPage(1);
        setOcrEndPage(Math.min(total_pages, 5));
        setOcrStep("range");
        toast.success(`Scanned PDF: ${total_pages} pages. Select page range.`);
      }
    } catch (error) {
      toast.error(formatApiError(error, "Failed to analyze PDF"));
    } finally {
      setLoading(false);
    }
  };

  const handleStartOCR = async () => {
    if (!sessionKey) {
      toast.error("Session expired. Please upload again.");
      resetOCRState();
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("session_key", sessionKey);
      formData.append("start_page", ocrStartPage.toString());
      formData.append("end_page", ocrEndPage.toString());

      const response = await axios.post(`${API}/contracts/ocr/start`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOcrJobId(response.data.job_id);
      setOcrStatus({ status: "pending", processed_pages: 0, total_pages: ocrEndPage - ocrStartPage + 1 });
      setOcrStep("processing");
      toast.success("OCR started!");
    } catch (error) {
      toast.error(formatApiError(error, "Failed to start OCR"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOCR = async () => {
    if (!ocrJobId) return;
    
    try {
      await axios.post(`${API}/contracts/ocr/cancel/${ocrJobId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info("Cancelling OCR...");
    } catch (error) {
      toast.error(formatApiError(error, "Failed to cancel"));
    }
  };

  const handleResumeOCR = async () => {
    if (!ocrJobId) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contracts/ocr/resume/${ocrJobId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOcrStep("processing");
      toast.success("Resuming OCR...");
    } catch (error) {
      toast.error(formatApiError(error, "Failed to resume"));
    } finally {
      setLoading(false);
    }
  };

  const handleOCRMorePages = async () => {
    if (!ocrJobId) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("additional_pages", "5");
      
      const response = await axios.post(`${API}/contracts/ocr/more/${ocrJobId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOcrJobId(response.data.job_id);
      setOcrStep("processing");
      toast.success(`Processing ${response.data.total_pages} more pages...`);
    } catch (error) {
      toast.error(formatApiError(error, "Failed to process more pages"));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOCR = async () => {
    if (!ocrEditedText.trim() || !contractName) {
      toast.error("Please enter text and contract name");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/contracts/save-ocr`, {
        name: contractName,
        text: ocrEditedText,
        job_id: ocrJobId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Contract saved!");
      navigate(`/ask/${response.data.id}`);
    } catch (error) {
      toast.error(formatApiError(error, "Failed to save"));
    } finally {
      setLoading(false);
    }
  };

  const handlePasteUpload = async () => {
    if (!pasteText.trim() || !contractName) {
      toast.error("Please enter text and name");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/contracts/paste-text`, {
        name: contractName,
        content: pasteText,
        upload_type: "paste"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Contract saved!");
      navigate(`/ask/${response.data.id}`);
    } catch (error) {
      toast.error(formatApiError(error, "Save failed"));
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate count (max 20)
    if (selectedFiles.length + photos.length > 20) {
      toast.error("Maximum 20 photos allowed. Please select fewer images.");
      return;
    }
    
    // Validate file sizes and types
    const validFiles = [];
    for (const file of selectedFiles) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB per photo)`);
        continue;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    // Create preview URLs
    const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
    
    setPhotos([...photos, ...validFiles]);
    setPhotoPreviewUrls([...photoPreviewUrls, ...newPreviewUrls]);
    
    toast.success(`Added ${validFiles.length} photo(s)`);
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    const newUrls = photoPreviewUrls.filter((_, i) => i !== index);
    
    // Revoke old URL to free memory
    URL.revokeObjectURL(photoPreviewUrls[index]);
    
    setPhotos(newPhotos);
    setPhotoPreviewUrls(newUrls);
  };

  const movePhotoUp = (index) => {
    if (index === 0) return;
    
    const newPhotos = [...photos];
    const newUrls = [...photoPreviewUrls];
    
    [newPhotos[index], newPhotos[index - 1]] = [newPhotos[index - 1], newPhotos[index]];
    [newUrls[index], newUrls[index - 1]] = [newUrls[index - 1], newUrls[index]];
    
    setPhotos(newPhotos);
    setPhotoPreviewUrls(newUrls);
  };

  const movePhotoDown = (index) => {
    if (index === photos.length - 1) return;
    
    const newPhotos = [...photos];
    const newUrls = [...photoPreviewUrls];
    
    [newPhotos[index], newPhotos[index + 1]] = [newPhotos[index + 1], newPhotos[index]];
    [newUrls[index], newUrls[index + 1]] = [newUrls[index + 1], newUrls[index]];
    
    setPhotos(newPhotos);
    setPhotoPreviewUrls(newUrls);
  };

  const handlePhotoUpload = async () => {
    if (!photos.length) {
      toast.error("Please select at least one photo");
      return;
    }
    
    if (!contractName) {
      toast.error("Please enter a contract name");
      return;
    }
    
    setUploadingPhotos(true);
    setPhotoProgress({ current: 0, total: photos.length });
    
    try {
      const formData = new FormData();
      formData.append("name", contractName);
      
      photos.forEach((photo) => {
        formData.append("files", photo);
      });
      
      toast.info(`Uploading ${photos.length} photo(s)...`);
      
      const response = await axios.post(`${API}/contracts/upload-photos`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
        timeout: 600000, // 10 minutes for OCR processing
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
          
          if (percentCompleted === 100) {
            toast.info("Photos uploaded. Running OCR...");
          }
        }
      });
      
      const { pages_processed, total_photos, failed_pages } = response.data;
      
      if (failed_pages && failed_pages.length > 0) {
        toast.warning(`Processed ${pages_processed}/${total_photos} photos. Failed pages: ${failed_pages.join(', ')}`);
      } else {
        toast.success(`All ${pages_processed} photos processed successfully!`);
      }
      
      // Clean up preview URLs
      photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
      
      navigate(`/ask/${response.data.id}`);
    } catch (error) {
      console.error("Photo upload error:", error);
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error("Processing timed out. Please try with fewer photos or better quality images.");
      } else {
        toast.error(formatApiError(error, "Photo upload failed. Please try again."));
      }
    } finally {
      setUploadingPhotos(false);
      setPhotoProgress({ current: 0, total: 0 });
    }
  };

  const resetAll = () => {
    setFile(null);
    setContractName("");
    setPhotos([]);
    photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
    setPhotoPreviewUrls([]);
    resetOCRState();
  };

  const progress = ocrStatus ? (ocrStatus.processed_pages / ocrStatus.total_pages * 100) : 0;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2" data-testid="upload-title">
            Upload Contract
          </h1>
          <p className="text-muted-foreground">Your document stays private.</p>
        </div>

        <div className="mb-6">
          <Label htmlFor="contractName">Contract Name</Label>
          <Input
            id="contractName"
            placeholder="e.g., SEIU Local 1000 - 2024 Agreement"
            value={contractName}
            onChange={(e) => setContractName(e.target.value)}
            className="mt-2 bg-card"
            data-testid="contract-name-input"
          />
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetAll(); }}>
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="file" data-testid="tab-file"><FileText className="w-4 h-4 mr-1" /> File</TabsTrigger>
            <TabsTrigger value="scanned" data-testid="tab-scanned"><ScanLine className="w-4 h-4 mr-1" /> Scanned</TabsTrigger>
            <TabsTrigger value="camera" data-testid="tab-camera"><Image className="w-4 h-4 mr-1" /> Camera</TabsTrigger>
            <TabsTrigger value="image" data-testid="tab-image"><Image className="w-4 h-4 mr-1" /> Photo</TabsTrigger>
            <TabsTrigger value="paste" data-testid="tab-paste"><Type className="w-4 h-4 mr-1" /> Paste</TabsTrigger>
          </TabsList>

          {/* File Upload */}
          <TabsContent value="file">
            <div
              className={`upload-zone ${dragActive ? "dragging" : ""} ${file ? "border-primary" : ""}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="file-preview">
                  <div className="file-preview-icon"><FileText className="w-5 h-5" /></div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <UploadIcon className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="font-medium mb-1">Drop your file here</p>
                  <p className="text-sm text-muted-foreground mb-3">PDF, DOCX, or TXT (max 20MB)</p>
                  <label>
                    <input type="file" className="hidden" accept=".pdf,.docx,.txt" onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} />
                    <Button variant="outline" size="sm" className="cursor-pointer" asChild><span>Browse</span></Button>
                  </label>
                </>
              )}
            </div>
            {ocrWarning && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800">{ocrWarning}</p>
              </div>
            )}
            <Button className="w-full mt-4 btn-lift" disabled={!file || !contractName || loading} onClick={handleFileUpload}>
              {loading ? "Processing..." : "Upload Contract"}
            </Button>
          </TabsContent>

          {/* Scanned PDF */}
          <TabsContent value="scanned">
            {ocrStep === "upload" && (
              <>
                <div
                  className={`upload-zone ${dragActive ? "dragging" : ""} ${file ? "border-primary" : ""}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {file ? (
                    <div className="file-preview">
                      <div className="file-preview-icon"><ScanLine className="w-5 h-5" /></div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-sm">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button onClick={() => setFile(null)}><X className="w-5 h-5" /></button>
                    </div>
                  ) : (
                    <>
                      <ScanLine className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                      <p className="font-medium mb-1">Drop scanned PDF here</p>
                      <p className="text-sm text-muted-foreground mb-3">We'll use OCR to extract text</p>
                      <label>
                        <input type="file" className="hidden" accept=".pdf" onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} />
                        <Button variant="outline" size="sm" className="cursor-pointer" asChild><span>Browse PDFs</span></Button>
                      </label>
                    </>
                  )}
                </div>
                {ocrWarning && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800">{ocrWarning}</p>
                  </div>
                )}
                <Button className="w-full mt-4 btn-lift" disabled={!file || loading} onClick={handleAnalyzePDF}>
                  {loading ? "Analyzing..." : "Analyze PDF"}
                </Button>
              </>
            )}

            {ocrStep === "range" && (
              <div className="bg-card border border-border rounded-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ScanLine className="w-5 h-5 text-primary" />
                  <h3 className="font-medium">Select Pages for OCR</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  <strong>{ocrTotalPages} pages</strong> detected. Process in batches for best results.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Start Page</Label>
                    <Input
                      type="number"
                      min={1}
                      max={ocrTotalPages}
                      value={ocrStartPage}
                      onChange={(e) => setOcrStartPage(Math.max(1, Math.min(ocrTotalPages, parseInt(e.target.value) || 1)))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>End Page</Label>
                    <Input
                      type="number"
                      min={1}
                      max={ocrTotalPages}
                      value={ocrEndPage}
                      onChange={(e) => setOcrEndPage(Math.max(1, Math.min(ocrTotalPages, parseInt(e.target.value) || ocrTotalPages)))}
                      className="mt-1"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Processing {ocrEndPage - ocrStartPage + 1} page(s). Start with fewer pages for faster results.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={resetAll}>Back</Button>
                  <Button className="flex-1 btn-lift" disabled={loading || ocrStartPage > ocrEndPage} onClick={handleStartOCR}>
                    {loading ? "Starting..." : `Start OCR (${ocrEndPage - ocrStartPage + 1} pages)`}
                  </Button>
                </div>
              </div>
            )}

            {ocrStep === "processing" && (
              <div className="bg-card border border-border rounded-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ScanLine className="w-5 h-5 text-primary animate-pulse" />
                    <h3 className="font-medium">Processing OCR</h3>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleCancelOCR}>
                    <Pause className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Page {ocrStatus?.current_page || ocrStatus?.processed_pages || 0} of {ocrStatus?.total_pages || 0}</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>

                {ocrStatus?.completed_pages?.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    Completed: {ocrStatus.completed_pages.join(", ")}
                  </div>
                )}
                
                {ocrStatus?.failed_pages?.length > 0 && (
                  <div className="mt-2 text-xs text-amber-600">
                    Failed: {ocrStatus.failed_pages.map(p => p.page).join(", ")}
                  </div>
                )}
              </div>
            )}

            {ocrStep === "preview" && (
              <div className="space-y-4">
                {/* Status info */}
                <div className={`p-4 rounded-md border ${ocrStatus?.status === "completed" ? "bg-primary/5 border-primary/20" : ocrStatus?.status === "cancelled" ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"}`}>
                  <div className="flex items-start gap-2">
                    {ocrStatus?.status === "completed" ? (
                      <Check className="w-5 h-5 text-primary mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{ocrStatus?.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {ocrStatus?.processed_pages || 0} pages processed • {ocrEditedText.length.toLocaleString()} characters
                      </p>
                    </div>
                  </div>
                </div>

                {/* Failed pages warning */}
                {ocrStatus?.failed_pages?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                    <p className="text-sm font-medium text-amber-800">Failed Pages</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Pages {ocrStatus.failed_pages.map(p => p.page).join(", ")} failed. 
                      Consider uploading these as individual photos.
                    </p>
                  </div>
                )}

                {/* Page confidence */}
                {ocrStatus?.page_results?.length > 0 && (
                  <div className="bg-secondary/30 rounded-md p-3">
                    <p className="text-sm font-medium mb-2">Page Confidence</p>
                    <div className="flex flex-wrap gap-2">
                      {ocrStatus.page_results.map((pr) => (
                        <span 
                          key={pr.page}
                          className={`text-xs px-2 py-1 rounded ${
                            pr.confidence >= 80 ? 'bg-emerald-100 text-emerald-700' :
                            pr.confidence >= 60 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}
                        >
                          P{pr.page}: {pr.confidence}%
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Editable text */}
                <div>
                  <div className="flex justify-between mb-2">
                    <Label>Extracted Text (editable)</Label>
                    <span className="text-xs text-muted-foreground">{ocrEditedText.length.toLocaleString()} chars</span>
                  </div>
                  <Textarea
                    value={ocrEditedText}
                    onChange={(e) => setOcrEditedText(e.target.value)}
                    className="min-h-[250px] bg-card font-mono text-sm"
                    placeholder="OCR text appears here..."
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={resetAll}>
                    <RotateCcw className="w-4 h-4 mr-1" /> Start Over
                  </Button>
                  
                  {ocrStatus?.can_resume && (
                    <Button variant="outline" onClick={handleResumeOCR} disabled={loading}>
                      <Play className="w-4 h-4 mr-1" /> Resume
                    </Button>
                  )}
                  
                  {ocrStatus?.status === "completed" && ocrEndPage < ocrTotalPages && (
                    <Button variant="outline" onClick={handleOCRMorePages} disabled={loading}>
                      More Pages
                    </Button>
                  )}
                  
                  <Button 
                    className="flex-1 btn-lift" 
                    disabled={!ocrEditedText.trim() || !contractName || loading}
                    onClick={handleSaveOCR}
                  >
                    {loading ? "Saving..." : "Save Contract"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Camera Upload (Multi-Photo) */}
          <TabsContent value="camera">
            <div className="space-y-4">
              {/* Instructions */}
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md p-4">
                <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-2">
                  📸 Camera Tips for Best Results:
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
                  <li>Take photos in good lighting (avoid shadows and glare)</li>
                  <li>Include the full page in each photo</li>
                  <li>Hold camera steady for clear text</li>
                  <li>Take photos in page order for easier processing</li>
                  <li>Maximum 20 photos per upload (10MB per photo)</li>
                </ul>
              </div>

              {/* Photo Selection */}
              <div className="upload-zone">
                <Image className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                <p className="font-medium mb-1">Capture Contract Pages</p>
                <p className="text-sm text-muted-foreground mb-3">
                  {photos.length === 0 ? "Use camera or select photos" : `${photos.length} photo(s) selected`}
                </p>
                <label>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={handlePhotoSelect}
                  />
                  <Button variant="outline" size="sm" className="cursor-pointer" asChild>
                    <span>📷 Use Camera / Choose Photos</span>
                  </Button>
                </label>
              </div>

              {/* Photo Thumbnails */}
              {photos.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Photos ({photos.length}/20)</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
                        setPhotos([]);
                        setPhotoPreviewUrls([]);
                      }}
                    >
                      Clear All
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {photos.map((photo, index) => (
                      <div key={`photo-${photo.name}-${photo.size}-${index}`} className="relative group border border-border rounded-md overflow-hidden bg-card">
                        <img
                          src={photoPreviewUrls[index]}
                          alt={`Page ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          Page {index + 1}
                        </div>
                        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => movePhotoUp(index)}
                            disabled={index === 0}
                            className="bg-black/70 text-white p-1 rounded disabled:opacity-30"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => movePhotoDown(index)}
                            disabled={index === photos.length - 1}
                            className="bg-black/70 text-white p-1 rounded disabled:opacity-30"
                          >
                            ↓
                          </button>
                          <button
                            onClick={() => removePhoto(index)}
                            className="bg-red-600 text-white p-1 rounded"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="p-2 text-xs text-muted-foreground truncate">
                          {photo.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploadingPhotos && (
                <div className="bg-card border border-border rounded-md p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ScanLine className="w-4 h-4 text-primary animate-spin" />
                    <p className="text-sm font-medium">Processing photos with OCR...</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This may take a few minutes depending on photo quality and quantity.
                  </p>
                </div>
              )}

              {/* Upload Button */}
              <Button
                className="w-full btn-lift"
                disabled={photos.length === 0 || !contractName || uploadingPhotos}
                onClick={handlePhotoUpload}
              >
                {uploadingPhotos ? `Processing ${photos.length} photo(s)...` : `Upload ${photos.length} Photo(s) & Extract Text`}
              </Button>
            </div>
          </TabsContent>

          {/* Image Upload */}
          <TabsContent value="image">
            <div
              className={`upload-zone ${dragActive ? "dragging" : ""} ${file ? "border-primary" : ""}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="file-preview">
                  <div className="file-preview-icon"><Image className="w-5 h-5" /></div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button onClick={() => setFile(null)}><X className="w-5 h-5" /></button>
                </div>
              ) : (
                <>
                  <Image className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="font-medium mb-1">Drop photo here</p>
                  <p className="text-sm text-muted-foreground mb-3">JPEG, PNG, or WebP (max 20MB)</p>
                  <label>
                    <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} />
                    <Button variant="outline" size="sm" className="cursor-pointer" asChild><span>Browse</span></Button>
                  </label>
                </>
              )}
            </div>
            <Button className="w-full mt-4 btn-lift" disabled={!file || !contractName || loading} onClick={handleFileUpload}>
              {loading ? "Processing..." : "Upload & Extract"}
            </Button>
          </TabsContent>

          {/* Paste Text */}
          <TabsContent value="paste">
            <Textarea
              placeholder="Paste contract text here..."
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              className="min-h-[300px] bg-card"
            />
            <p className="text-xs text-muted-foreground mt-2">{pasteText.length.toLocaleString()} characters</p>
            <Button className="w-full mt-4 btn-lift" disabled={!pasteText.trim() || !contractName || loading} onClick={handlePasteUpload}>
              {loading ? "Saving..." : "Save Contract"}
            </Button>
          </TabsContent>
        </Tabs>

        <div className="mt-6 p-4 bg-secondary/50 rounded-sm">
          <p className="text-sm text-muted-foreground">
            <strong>Privacy:</strong> Your document is private and never shared.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Upload;
